import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { fmtDuration } from '../lib/format';
import { CallDetailPanel, type CallRow } from './CallDetailPanel';
import type { AgentCallRow } from '../hooks/useAgentCalls';

/** Dashboard → assistant calls → one call. Associates the provider's call (by
 *  id) with our stored Call row (`conversationId`); when we have it, shows the
 *  same rich CallDetailPanel as the Calls page. When we don't, falls back to the
 *  basic provider stats. `onBack` returns to the assistant's call list (the
 *  split is a single panel, so the detail replaces it). */
export function DashboardCallDetailPanel({
  conversationId,
  fallback,
  onBack,
}: {
  conversationId: string;
  fallback: AgentCallRow;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const { data, isLoading } = crud.call.useFindUnique(
    { where: { conversationId }, include: { specialist: { include: { user: true } }, transcript: true } },
    { staleTime: 30_000, retry: false },
  );
  const call = (data as CallRow | null | undefined) ?? null;

  const dtFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  const BackBar = (
    <button
      type="button"
      onClick={onBack}
      className="shrink-0 flex items-center gap-1.5 border-b border-border px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('dashboard.callsPanel.back')}
    </button>
  );

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        {BackBar}
        <div className="flex-1 p-4 text-sm text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // We have the stored Call → reuse the full Calls-page detail.
  if (call) {
    return (
      <div className="flex h-full flex-col">
        {BackBar}
        <div className="min-h-0 flex-1">
          <CallDetailPanel call={call} />
        </div>
      </div>
    );
  }

  // Not recorded on our side — show what the provider gave us.
  return (
    <div className="flex h-full flex-col">
      {BackBar}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          {t('dashboard.callsPanel.notStored')}
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Stat
            label={t('dashboard.callsPanel.when')}
            value={fallback.startedAt ? dtFmt.format(new Date(fallback.startedAt)) : '—'}
          />
          <Stat label={t('dashboard.callsPanel.status')} value={fallback.endedReason || fallback.status || '—'} />
          <Stat label={t('dashboard.cards.time')} value={fmtDuration(fallback.durationSec)} />
        </div>
        <div className="pt-2 border-t border-border text-[11px] text-muted-foreground truncate">
          Conversation: {conversationId}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-2">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}
